// Developer-task generation for deep CLS results.
// Extracted from quickAudit.js so the SERVER-side complex-audit orchestrator
// (server/services/complexAudit.js) can import it without pulling in the
// browser-only API client (services/api.js) that quickAudit.js depends on.
// Both the browser quick-audit flow and the server worker use this module —
// keep it free of DOM/browser APIs.

export const generateClsTasks = (deepCLSResults, language) => {
  const tasks = [];
  const addedCauses = new Set();

  if (deepCLSResults?.elements?.length > 0) {
    const topShifters = [...deepCLSResults.elements]
      .sort((a, b) => (b.totalShift || 0) - (a.totalShift || 0))
      .slice(0, 5);

    topShifters.forEach(shifter => {
      const cause = shifter.cause || 'unknown';
      if (addedCauses.has(cause)) return;

      const isSignificant = shifter.totalShift > 0.05;
      if (!isSignificant) return;

      const priority = shifter.totalShift > 0.5 ? 'critical' : shifter.totalShift > 0.1 ? 'high' : 'medium';

      if (cause === 'dynamic-content' || cause === 'unknown') {
        addedCauses.add(cause);
        tasks.push({
          priority,
          title: language === 'cs' ? 'Opravit dynamický widget' : 'Fix dynamic widget',
          selector: shifter.selector,
          description: language === 'cs'
            ? `Widget "${shifter.selector}" nemá rezervované místo a způsobuje layout shift ${shifter.totalShift?.toFixed(3)}.`
            : `Widget "${shifter.selector}" has no reserved space and causes layout shift of ${shifter.totalShift?.toFixed(3)}.`,
          fix: language === 'cs'
            ? `1. Přidejte min-height nebo aspect-ratio kontejner\n2. Pokud jde o reklamu/embed, použijte placeholder\n3. Načítejte obsah lazy až pod foldem`
            : `1. Add min-height or aspect-ratio container\n2. If it's an ad/embed, use a placeholder\n3. Load content lazily below the fold`
        });
      }

      if (cause === 'iframe-embed' || cause === 'video-embed') {
        addedCauses.add(cause);
        tasks.push({
          priority,
          title: language === 'cs' ? 'Rezervovat místo pro embed' : 'Reserve space for embed',
          selector: shifter.selector,
          description: language === 'cs'
            ? `Vložený obsah (iframe/video) nemá definované rozměry.`
            : `Embedded content (iframe/video) has no defined dimensions.`,
          fix: language === 'cs'
            ? `1. Obalte element kontejnerem s aspect-ratio: 16/9\n2. Nebo přidejte width a height atributy\n3. Pro lazy-loading použijte loading="lazy"`
            : `1. Wrap element in container with aspect-ratio: 16/9\n2. Or add width and height attributes\n3. For lazy loading use loading="lazy"`
        });
      }

      if (cause === 'unsized-image' || cause === 'image-load') {
        addedCauses.add(cause);
        tasks.push({
          priority,
          title: language === 'cs' ? 'Přidat rozměry obrázkům' : 'Add image dimensions',
          selector: shifter.selector,
          description: language === 'cs'
            ? `Obrázky bez width/height atributů způsobují layout shift.`
            : `Images without width/height attributes cause layout shift.`,
          fix: language === 'cs'
            ? `1. Přidejte width a height atributy ke všem <img>\n2. Použijte aspect-ratio v CSS\n3. Pro responzivní obrázky: srcset + sizes`
            : `1. Add width and height attributes to all <img>\n2. Use aspect-ratio in CSS\n3. For responsive images: srcset + sizes`
        });
      }
    });
  }

  if (deepCLSResults?.phases?.scroll > 0.1) {
    tasks.push({
      priority: deepCLSResults.phases.scroll > 0.5 ? 'critical' : 'high',
      title: language === 'cs' ? 'Opravit shifty při scrollu' : 'Fix scroll-triggered shifts',
      description: language === 'cs'
        ? `${(deepCLSResults.phases.scroll).toFixed(3)} CLS vzniká až při scrollování - lazy-loaded obsah.`
        : `${(deepCLSResults.phases.scroll).toFixed(3)} CLS occurs during scrolling - lazy-loaded content.`,
      fix: language === 'cs'
        ? `1. Přidejte skeleton loadery pro lazy obsah\n2. Rezervujte místo pomocí min-height\n3. Použijte content-visibility s contain-intrinsic-size`
        : `1. Add skeleton loaders for lazy content\n2. Reserve space with min-height\n3. Use content-visibility with contain-intrinsic-size`
    });
  }

  return tasks;
};
